import { z } from 'zod';
import {
  DEFAULT_WEEKLY_BRIEF_MIN_TOPIC_VOLUME,
  DEFAULT_WEEKLY_BRIEF_MIN_WEEKLY_VOLUME,
  WEEKLY_BRIEF_HISTORY_PAGE_SIZE,
} from './weekly-brief.default';
import { WeeklyBriefStatus, WeeklyBriefTrigger } from './weekly-brief.enums';

const RecipientsSchema = z
  .array(z.string().trim().toLowerCase().pipe(z.email()))
  .transform((recipients) => [...new Set(recipients)]);

export const WeeklyBriefSettingsSchema = z.object({
  enabled: z.boolean(),
  requireApproval: z.boolean(),
  recipients: z.array(z.string()),
  minWeeklyVolume: z.number().int(),
  minTopicVolume: z.number().int(),
});

export const UpdateWeeklyBriefSettingsSchema = z.object({
  enabled: z.boolean(),
  requireApproval: z.boolean(),
  recipients: RecipientsSchema,
  minWeeklyVolume: z
    .number()
    .int()
    .min(1)
    .default(DEFAULT_WEEKLY_BRIEF_MIN_WEEKLY_VOLUME),
  minTopicVolume: z
    .number()
    .int()
    .min(1)
    .default(DEFAULT_WEEKLY_BRIEF_MIN_TOPIC_VOLUME),
});

// Name and email are copied at the moment of the action, so the history still
// shows who approved after the user is renamed or deleted.
export const WeeklyBriefActorSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
});

export const WeeklyBriefListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional().default(1),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(100)
    .optional()
    .default(WEEKLY_BRIEF_HISTORY_PAGE_SIZE),
});

export const WeeklyBriefSummarySchema = z.object({
  id: z.string(),
  weekStart: z.string(),
  weekLabel: z.string(),
  status: z.enum(WeeklyBriefStatus),
  trigger: z.enum(WeeklyBriefTrigger),
  reason: z.string().nullable(),
  createdAt: z.string(),
  sentAt: z.string().nullable(),
  sentToCount: z.number(),
  approvedBy: WeeklyBriefActorSchema.nullable(),
  approvedAt: z.string().nullable(),
  rejectedBy: WeeklyBriefActorSchema.nullable(),
  rejectedAt: z.string().nullable(),
});

export const WeeklyBriefListResponseSchema = z.object({
  items: z.array(WeeklyBriefSummarySchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export const WeeklyBriefDetailSchema = WeeklyBriefSummarySchema.extend({
  subject: z.string().nullable(),
  preview: z.string().nullable(),
  html: z.string().nullable(),
  sentTo: z.array(z.string()),
});

export const WeeklyBriefParamsSchema = z.object({
  briefId: z.string(),
});

export const WeeklyBriefSendTestResponseSchema = z.object({
  sentTo: z.string().nullable(),
  // Set when the week would have been skipped; the test is sent anyway when
  // there is a topic to write about.
  skipReason: z.string().nullable(),
});
